import {
  LockIcon,
  RetryIcon,
  SignInIcon,
  SignOutIcon,
  SpinnerIcon,
  WarningIcon,
} from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, type ReactNode } from "react";

/**
 * Lo que se ve cuando todavía no hay aplicación: entrando, o sin poder entrar.
 *
 * Van fuera de los dos armazones --no hay conmutador de modo que enseñar
 * porque no se está en ningún modo-- y centradas, como la pantalla de
 * arranque de antes.
 */
const SCREEN = "grid h-dvh place-items-center bg-background p-8";

/** Mientras se recupera la sesión o se va y vuelve del SSO. */
export function SessionPending({ children }: { readonly children: ReactNode }) {
  return (
    <div className={SCREEN} role="status">
      <p className="flex items-center gap-2 text-muted-foreground">
        <SpinnerIcon className="animate-spin" />
        {children}
      </p>
    </div>
  );
}

/**
 * No se pudo entrar, y se dice por qué.
 *
 * Siempre con una salida a mano: el SSO no se relanza solo desde aquí --eso
 * es justo lo que el anti-bucle impide--, pero la persona sí puede pedirlo.
 */
export function SessionProblem({
  title,
  reason,
  onSignIn,
  onSignOut,
}: {
  readonly title: string;
  readonly reason: ReactNode;
  readonly onSignIn?: () => void;
  readonly onSignOut?: () => void;
}) {
  return (
    <div className={SCREEN}>
      <Alert className="max-w-lg">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="grid gap-3">
          <div>{reason}</div>
          <div className="flex flex-wrap gap-2">
            {onSignIn !== undefined && (
              <Button size="sm" onClick={onSignIn}>
                <SignInIcon />
                Volver a entrar
              </Button>
            )}
            {onSignOut !== undefined && (
              <Button size="sm" variant="outline" onClick={onSignOut}>
                <SignOutIcon />
                Cerrar sesión
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * No se llegó al servidor al entrar: sin red, sin respuesta a tiempo o un
 * fallo suyo. No es falta de sesión --por eso no se va al SSO solo--, y se
 * ofrecen las dos salidas: reintentar, que es lo normal y va con Enter, o ir
 * al inicio de sesión a mano.
 */
export function ServerUnreachable({
  reason,
  reference,
  onRetry,
  onSignIn,
}: {
  readonly reason: string;
  readonly reference?: string | undefined;
  readonly onRetry: () => void;
  readonly onSignIn: () => void;
}) {
  const retry = useRef<HTMLButtonElement>(null);

  /* Enter reintenta aunque el foco se haya ido del botón; sobre el otro
     botón, Enter es suyo. */
  useEffect(() => {
    retry.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.defaultPrevented) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      if (event.target instanceof HTMLButtonElement) return;
      event.preventDefault();
      onRetry();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onRetry]);

  return (
    <div className={SCREEN}>
      <Alert className="max-w-lg" role="alert">
        <WarningIcon />
        <AlertTitle>No pudimos conectar con el servidor</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>{reason}</p>
          <p>
            Puede ser la conexión o que el servidor no esté disponible ahora.
            Prueba otra vez en un momento.
          </p>
          {reference !== undefined && (
            <p className="text-xs">
              Referencia: <span className="font-mono">{reference}</span>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button ref={retry} size="sm" onClick={onRetry}>
              <RetryIcon />
              Reintentar
              <kbd className="ml-1 rounded border border-primary-foreground/30 px-1 font-sans text-[0.68rem] opacity-80">
                Enter
              </kbd>
            </Button>
            <Button size="sm" variant="outline" onClick={onSignIn}>
              <SignInIcon />
              Ir al inicio de sesión
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * Hay sesión, pero no para esta aplicación: falta `nutriexpertos:global.view`.
 *
 * Se dice CON QUÉ CUENTA se entró, porque lo más probable es que sea la
 * equivocada --el SSO de lala-ms es el mismo para todas sus aplicaciones--, y
 * se ofrece salir para entrar con otra.
 */
export function AccessDenied({
  email,
  onSignOut,
}: {
  readonly email: string;
  readonly onSignOut: () => void;
}) {
  return (
    <div className={SCREEN}>
      <Alert className="max-w-lg">
        <LockIcon />
        <AlertTitle>No tienes acceso a Nutriexpertos</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>
            Has entrado como <strong>{email || "una cuenta sin correo"}</strong>
            , y esa cuenta no tiene permiso para usar esta aplicación. Si crees
            que debería, pídeselo a quien administra el programa; si no es tu
            cuenta de trabajo, sal y entra con otra.
          </p>
          <div>
            <Button size="sm" variant="outline" onClick={onSignOut}>
              <SignOutIcon />
              Cerrar sesión
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
